import { useState, useEffect } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { accountsAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, MagnifyingGlass, Scales } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ChartOfAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    account_type: 'Asset',
    parent_code: '',
    is_group: false,
    gst_applicable: false,
  });

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await accountsAPI.list(filter !== 'all' ? filter : undefined);
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, [filter]);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await accountsAPI.create(formData);
      toast.success('Account created successfully');
      setDialogOpen(false);
      setFormData({ code: '', name: '', account_type: 'Asset', parent_code: '', is_group: false, gst_applicable: false });
      loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
  };

  const filteredAccounts = accounts.filter(acc => {
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        acc.code?.toLowerCase().includes(searchLower) ||
        acc.name?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const getTypeBadgeColor = (type) => {
    switch(type) {
      case 'Asset': return 'bg-blue-100 text-blue-700';
      case 'Liability': return 'bg-red-100 text-red-700';
      case 'Equity': return 'bg-purple-100 text-purple-700';
      case 'Revenue': return 'bg-green-100 text-green-700';
      case 'Expense': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Group accounts by type
  const groupedAccounts = filteredAccounts.reduce((acc, account) => {
    const type = account.account_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(account);
    return acc;
  }, {});

  return (
    <div data-testid="chart-of-accounts-page">
      <PageHeader 
        title="Chart of Accounts" 
        subtitle="India-compliant account structure"
        actions={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="add-account-btn">
                <Plus size={16} className="mr-2" />
                Add Account
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Account</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div>
                  <label className="form-label">Account Code</label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                    placeholder="e.g., 1001"
                    required
                    data-testid="account-code"
                  />
                </div>
                <div>
                  <label className="form-label">Account Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Cash in Hand"
                    required
                    data-testid="account-name"
                  />
                </div>
                <div>
                  <label className="form-label">Account Type</label>
                  <Select
                    value={formData.account_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, account_type: value }))}
                  >
                    <SelectTrigger data-testid="account-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asset">Asset</SelectItem>
                      <SelectItem value="Liability">Liability</SelectItem>
                      <SelectItem value="Equity">Equity</SelectItem>
                      <SelectItem value="Revenue">Revenue</SelectItem>
                      <SelectItem value="Expense">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="form-label">Parent Code (optional)</label>
                  <Input
                    value={formData.parent_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, parent_code: e.target.value }))}
                    placeholder="e.g., 1000"
                    data-testid="account-parent"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_group}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_group: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-sm">Is Group</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.gst_applicable}
                      onChange={(e) => setFormData(prev => ({ ...prev, gst_applicable: e.target.checked }))}
                      className="rounded"
                    />
                    <span className="text-sm">GST Applicable</span>
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="create-account-submit">
                    Create Account
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      
      <div className="page-content">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input
              placeholder="Search accounts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-accounts"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="filter-type">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Asset">Asset</SelectItem>
              <SelectItem value="Liability">Liability</SelectItem>
              <SelectItem value="Equity">Equity</SelectItem>
              <SelectItem value="Revenue">Revenue</SelectItem>
              <SelectItem value="Expense">Expense</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Accounts by Type */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton h-32 rounded-lg"></div>
            ))}
          </div>
        ) : Object.keys(groupedAccounts).length > 0 ? (
          <div className="space-y-6">
            {['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'].map(type => {
              const typeAccounts = groupedAccounts[type];
              if (!typeAccounts || typeAccounts.length === 0) return null;
              
              return (
                <div key={type} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
                  <div className="p-4 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${getTypeBadgeColor(type)}`}>{type}</span>
                        <span className="text-[#64748B] text-sm">({typeAccounts.length} accounts)</span>
                      </h3>
                      <span className="font-medium">
                        {formatCurrency(typeAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0))}
                      </span>
                    </div>
                  </div>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Parent</th>
                        <th>GST</th>
                        <th className="text-right">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {typeAccounts.map((account) => (
                        <tr key={account.id} data-testid={`account-row-${account.code}`}>
                          <td className="font-mono text-sm font-medium">{account.code}</td>
                          <td>
                            {account.is_group && <span className="text-xs text-[#64748B] mr-1">[Group]</span>}
                            {account.name}
                          </td>
                          <td className="text-[#64748B]">{account.parent_code || '-'}</td>
                          <td>
                            {account.gst_applicable ? (
                              <span className="text-xs text-green-600">Yes</span>
                            ) : (
                              <span className="text-xs text-[#64748B]">-</span>
                            )}
                          </td>
                          <td className="text-right font-medium">{formatCurrency(account.balance || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
            <Scales size={48} className="mx-auto text-[#64748B] mb-4" />
            <h3 className="text-lg font-medium mb-2">No Accounts Found</h3>
            <p className="text-[#64748B] mb-4">Seed the chart of accounts from the dashboard to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
